import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SEO, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { AlertCircle } from "lucide-react";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col app-surface">
      <SEO
        title="Refund & Cancelation Policy — Email2Contact"
        description="Email2Contact refund and cancelation policy: EU 14-day rights, credit top-up rules, subscription cancelation, grace period, and dispute resolution."
        path="/refund-policy"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Refund Policy", path: "/refund-policy" },
        ])}
      />
      <Navbar />
      <main className="flex-grow container px-4 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">Refund & Cancelation Policy</h1>
          <p className="text-muted-foreground mb-10">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="space-y-8">
            {/* EU 14-Day Notice */}
            <Card className="app-tile">
              <CardHeader>
                <CardTitle className="app-section-title flex items-start gap-2 text-lg">
                  <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  EU Consumer Rights (14-Day Withdrawal Period)
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p>
                  If you are a consumer based in the European Union, you have the right to withdraw from your purchase within 14 days without giving any reason, in accordance with the EU Consumer Rights Directive (2011/83/EU).
                </p>
                <p className="mt-2">
                  However, by using our service immediately after purchase (e.g., consuming AI credits), you expressly agree to waive your right of withdrawal as the service has been fully performed with your prior consent. Credits consumed during the 14-day period are non-refundable.
                </p>
              </CardContent>
            </Card>

            {/* Credit Purchases */}
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">1. Credit Purchases & Top-Ups</h2>
              
              <h3 className="text-lg font-semibold mb-2">1.1 Final and Non-Refundable</h3>
              <p className="text-muted-foreground mb-4">
                All AI credit purchases (subscription credits and one-time top-ups) are <strong>final and non-refundable</strong> once the payment has been processed. Credits are digital goods that are delivered instantly and consumed upon use.
              </p>

              <h3 className="text-lg font-semibold mb-2">1.2 Exceptions</h3>
              <p className="text-muted-foreground mb-4">
                Refunds may be granted at our sole discretion in the following exceptional circumstances:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Technical errors resulting in duplicate charges</li>
                <li>Service outages preventing credit usage for more than 48 consecutive hours</li>
                <li>Credits purchased but not yet consumed (subject to verification)</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2 mt-4">1.3 Credit Expiration</h3>
              <p className="text-muted-foreground mb-4">
                All credits (subscription and top-up) are tied to your active subscription and will expire under the following conditions:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Monthly renewal:</strong> At the beginning of each billing cycle, your credit balance resets to your plan's monthly allowance. Unused credits (including top-ups) do not roll over.</li>
                <li><strong>Subscription cancellation:</strong> Credits are no longer accessible once your subscription ends. Any remaining balance will expire at the end of your billing period.</li>
              </ul>
            </section>

            {/* Subscription Cancelation */}
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">2. Subscription Cancelation</h2>
              
              <h3 className="text-lg font-semibold mb-2">2.1 How to Cancel</h3>
              <p className="text-muted-foreground mb-4">
                You can cancel your subscription at any time through your account settings. Cancelation takes effect at the end of your current billing period.
              </p>

              <h3 className="text-lg font-semibold mb-2">2.2 Effect of Cancelation</h3>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Your subscription remains active until the end of the current billing period, but credits cannot be used after cancellation</li>
                <li>All credits (subscription and top-up) expire immediately when your subscription ends</li>
                <li>No refunds will be issued for unused time or credits in the current billing period</li>
                <li>You can reactivate your subscription at any time</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2 mt-4">2.3 Payment Failure Grace Period</h3>
              <p className="text-muted-foreground mb-4">
                If a scheduled payment fails (e.g., due to an expired or declined card), you will receive a <strong>3-day grace period</strong>:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Day 1:</strong> Payment failure notification with instructions to update your payment method</li>
                <li><strong>Day 2:</strong> First reminder email</li>
                <li><strong>Day 3:</strong> Final warning email</li>
                <li><strong>After 3 days:</strong> All remaining credits expire and your subscription is paused</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                During the grace period, you can continue using your remaining credits normally. Update your payment details in the Customer Portal to avoid credit expiration.
              </p>

              <h3 className="text-lg font-semibold mb-2 mt-4">2.4 Annual Subscriptions</h3>
              <p className="text-muted-foreground mb-4">
                Annual subscriptions are billed in full at the start of the subscription year. If you cancel an annual subscription:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Cancelation takes effect at the end of the 12-month period</li>
                <li>No pro-rated refunds are provided for the remaining months</li>
                <li>All benefits remain active until the subscription expires</li>
              </ul>
            </section>

            {/* Disputes */}
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">3. Dispute Resolution</h2>
              
              <h3 className="text-lg font-semibold mb-2">3.1 Contact Us First</h3>
              <p className="text-muted-foreground mb-4">
                If you have concerns about a charge or believe you are entitled to a refund, please contact us at <a href="mailto:support@email2contact.com" className="text-primary underline hover:text-primary/80">support@email2contact.com</a> before initiating a chargeback. We are committed to resolving billing issues quickly and fairly.
              </p>

              <h3 className="text-lg font-semibold mb-2">3.2 EU Online Dispute Resolution</h3>
              <p className="text-muted-foreground mb-4">
                EU consumers can access the European Commission's Online Dispute Resolution (ODR) platform at: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">https://ec.europa.eu/consumers/odr</a>
              </p>

              <h3 className="text-lg font-semibold mb-2">3.3 Chargeback Policy</h3>
              <p className="text-muted-foreground mb-4">
                Initiating a chargeback without contacting us first may result in:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Immediate suspension of your account and services</li>
                <li>Loss of all unused credits</li>
                <li>Potential fees to cover chargeback costs</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Chargebacks should be reserved for cases of fraud or unauthorized transactions. For legitimate billing disputes, please contact our support team.
              </p>
            </section>

            {/* Processing Times */}
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">4. Refund Processing</h2>
              <p className="text-muted-foreground mb-4">
                If a refund is approved:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Refunds will be processed within 5-10 business days</li>
                <li>Refunds will be issued to the original payment method</li>
                <li>You will receive an email confirmation once the refund has been processed</li>
                <li>Depending on your bank, it may take an additional 3-5 business days for the refund to appear in your account</li>
              </ul>
            </section>

            {/* Contact */}
            <section className="pt-8 border-t">
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">Questions?</h2>
              <p className="text-muted-foreground">
                If you have questions about our refund or cancelation policy, please contact us at <a href="mailto:support@email2contact.com" className="text-primary underline hover:text-primary/80">support@email2contact.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RefundPolicy;
